import { queryOptions } from '@tanstack/react-query';

import { featureFlagsControllerGetFeatureFlagActive } from '@/api/core-api/sdk.gen';
import { queryKeys } from '@/hooks/api/query-keys';
import { getClient } from '@/utils/get-api-client';

/**
 * Org-scoped core-api feature flag, safe to read in a route `beforeLoad`.
 *
 * Deliberately not `orgFeatureFlagQueryOptions` (`client/org-feature-flag-query-options.ts`),
 * which sends no client and so only carries auth in the browser (where the v4
 * proxy adds it from the session cookie). `beforeLoad` also runs during SSR —
 * without a server client the request goes out unauthenticated there, and a
 * flag gate would fail closed and redirect on every first page load. Passing
 * the isomorphic `getClient` makes it work on both sides: a real authenticated
 * client on the server, and the default browser client (via the v4 proxy) in
 * the browser.
 *
 * The query key matches `orgFeatureFlagQueryOptions`, so both share a cache
 * entry per `(organizationId, flagName)` — a component reading the same flag
 * with the browser-only hook will not issue a second request.
 *
 * @param organizationId - Active organization to scope the flag to.
 * @param flagName - Bare flag string (see
 *   `services/core-api/src/v4/features/feature-flags/feature-flags.md` — flags
 *   are runtime-only in core-api and never reach the generated OpenAPI client).
 * @param authUserId - Current user id, only used to build the server-side
 *   client; ignored in the browser.
 */
export const isomorphicOrgFeatureFlagQueryOptions = (
  organizationId: Maybe<string>,
  flagName: string,
  authUserId: Maybe<string>,
) =>
  queryOptions({
    queryKey: queryKeys.organizations.featureFlag(organizationId, flagName),
    queryFn: async (): Promise<boolean> => {
      const response = await featureFlagsControllerGetFeatureFlagActive({
        client: getClient(authUserId ?? undefined),
        path: { name: flagName },
        query: { organizationId: organizationId ?? '' },
        throwOnError: true,
      });
      return response.data === true;
    },
    enabled: !!organizationId,
  });
